import {
  HttpPortProtocol,
  Port,
  PortProtocol,
} from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import type {
  Environment,
  CreateInstanceRequest,
} from "components/Environment/utils/types";

export const LEGACY_NETWORK_ACCESS_PORT_ID_PREFIX = "legacy-network-access:";
const LEGACY_SSH_PORT = 22;

export type DisplayPortFields = {
  isEndpointPending?: boolean;
  hideAllowedSources?: boolean;
};

export type DisplayPort = Port & DisplayPortFields;

export function toDisplayPort(
  port: Port,
  displayFields: DisplayPortFields = {},
): DisplayPort {
  return Object.assign(port, displayFields);
}

export function toDisplayPorts(ports: readonly Port[]): DisplayPort[] {
  return ports.map((port) => toDisplayPort(port));
}

/**
 * Finds a built-in secure link, such as Jupyter, from the same DisplayPort rows
 * rendered in the Secure Links table. This intentionally does not use
 * findApplicationByHostnamePrefix because network-info ports are not full
 * IApplication objects.
 *
 * During Skybridge migration an environment can briefly have both a stale
 * Cloudflare hostname and the current Skybridge hostname for the same built-in
 * app. Prefer the non-brevlab hostname so the Settings CTA opens the endpoint
 * shown in Secure Links.
 */
export function findSecureLinkByHostnamePrefix(
  ports: readonly Pick<DisplayPort, "hostname" | "isEndpointPending">[],
  prefix: string,
  appIDCandidates: Array<string | undefined>,
): Pick<DisplayPort, "hostname" | "isEndpointPending"> | undefined {
  const ids = appIDCandidates.filter(
    (id): id is string => typeof id === "string" && id.length > 0,
  );
  const matches = ports.filter((port) => {
    if (port.isEndpointPending) return false;
    const name = port.hostname?.split(".")[0] ?? "";
    return (
      name.startsWith(prefix) &&
      (ids.length > 0 ? ids.some((id) => name.endsWith(`-${id}`)) : true)
    );
  });
  return (
    matches.find((port) => !port.hostname?.endsWith(".brevlab.com")) ??
    matches[0]
  );
}

type AccessPolicyLike = {
  access_policy?: {
    external_auth_id?: string;
    is_public?: boolean;
  };
  external_auth_id?: string;
  is_public?: boolean;
};

const getAccessPolicy = (policy: AccessPolicyLike) =>
  policy.access_policy ?? policy;

const getAuthorizedEmails = (policies: AccessPolicyLike[]) =>
  policies
    .map((policy) => getAccessPolicy(policy)?.external_auth_id)
    .filter(
      (externalAuthID): externalAuthID is string =>
        typeof externalAuthID === "string" && externalAuthID.length > 0,
    );

const isPublicIngress = (policies: AccessPolicyLike[]) =>
  policies.some((policy) => getAccessPolicy(policy)?.is_public === true);

const getHttpProtocol = (serviceType: string | undefined) =>
  serviceType?.toLowerCase() === "https"
    ? HttpPortProtocol.HTTPS
    : HttpPortProtocol.HTTP;

const getLegacyPortId = (kind: string, id: string) =>
  `${LEGACY_NETWORK_ACCESS_PORT_ID_PREFIX}${kind}:${id}`;

export const isLegacyNetworkAccessPort = (port: Pick<Port, "portId">) =>
  port.portId.startsWith(LEGACY_NETWORK_ACCESS_PORT_ID_PREFIX);

export function getLegacySecureLinkPorts(
  environment: Environment | null,
): DisplayPort[] {
  if (!environment) return [];

  const runtimeIngresses =
    environment.ingress_gateways?.flatMap(
      (gateway) => gateway.ingresses ?? [],
    ) ?? [];
  const buildIngresses =
    environment.build?.ingress_gateway?.flatMap(
      (gateway) => gateway.ingresses ?? [],
    ) ?? [];
  const ingresses =
    runtimeIngresses.length > 0 ? runtimeIngresses : buildIngresses;

  return ingresses.map((ingress, index) => {
    const accessPolicies = (ingress.access_policies ??
      []) as AccessPolicyLike[];
    const portNumber = Number(ingress.port) || 0;
    return toDisplayPort(
      new Port({
        portId: getLegacyPortId(
          "ingress",
          ingress.ingress_id || ingress.hostname || String(index),
        ),
        protocol: PortProtocol.UNSPECIFIED,
        httpProtocol: getHttpProtocol(ingress.service_type),
        portNumber,
        serverPort: portNumber,
        allowedSources: [],
        hostname: ingress.hostname,
        allowPublicUnauthenticated: isPublicIngress(accessPolicies),
        authorizedEmails: getAuthorizedEmails(accessPolicies),
      }),
      { isEndpointPending: true },
    );
  });
}

const getProvisionCreateInstanceRequest = (
  environment: Environment,
): CreateInstanceRequest | undefined =>
  environment.provision?.createInstanceRequest ??
  environment.provision?.Directive?.CreateInstanceRequest;

const hasProvisionFirewallRules = (environment: Environment) => {
  const createInstanceRequest = getProvisionCreateInstanceRequest(environment);
  return Boolean(
    createInstanceRequest?.firewall_rules_v2 ??
    createInstanceRequest?.firewall_rules,
  );
};

export function getLegacyFirewallPorts(
  environment: Environment | null,
): DisplayPort[] {
  if (!environment || !hasProvisionFirewallRules(environment)) return [];

  return [
    toDisplayPort(
      new Port({
        portId: getLegacyPortId("firewall", "ssh"),
        protocol: PortProtocol.TCP,
        httpProtocol: HttpPortProtocol.UNSPECIFIED,
        portNumber: LEGACY_SSH_PORT,
        serverPort: LEGACY_SSH_PORT,
        allowedSources: [],
        hostname: "",
        allowPublicUnauthenticated: false,
        authorizedEmails: [],
      }),
      { isEndpointPending: true },
    ),
  ];
}
